import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Phone, ShieldCheck, RotateCcw, ChevronLeft } from "lucide-react";
import Brand from "../../components/Brand";

function normalisePhone(raw) {
  const p = (raw || "").trim().replace(/[\s\-()]/g, "");
  if (!p) return "";
  if (p.startsWith("+")) return p;
  const digits = p.replace(/\D/g, "");
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
  return digits ? `+${digits}` : "";
}

export default function ScSignup() {
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [upi, setUpi] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const e164 = useMemo(() => normalisePhone(phone), [phone]);
  const phoneValid = e164.length >= 12 && e164.length <= 15;

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  async function requestOtp() {
    if (!phoneValid) { toast.error("Enter a valid phone number"); return; }
    setSending(true);
    try {
      const r = await api.post("/sc-auth/otp/request", { phone: e164, name: name || undefined });
      setDelivery(r.data?.delivery || "sms");
      setSecondsLeft(r.data?.expires_in_seconds || 300);
      setStep(2);
      toast.success(r.data?.delivery === "sms" ? "OTP sent via SMS" : "OTP generated (mock mode)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  }

  async function verifyOtp(e) {
    e?.preventDefault();
    if (code.trim().length < 4) { toast.error("Enter the 6-digit OTP"); return; }
    setVerifying(true);
    try {
      await api.post("/sc-auth/otp/verify", {
        phone: e164,
        code: code.trim(),
        name: name || undefined,
        city: city || undefined,
        upi_id: upi || undefined,
      });
      await refresh?.();
      toast.success("Welcome aboard!");
      nav("/sc", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setVerifying(false); }
  }

  return (
    <div className="min-h-screen bg-[#FFFCF7] text-[#0F2A14] relative overflow-hidden" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
      {/* Soft warm accents */}
      <div className="pointer-events-none absolute -top-32 -right-24 w-80 h-80 rounded-full bg-[#FFF4E5] blur-3xl"></div>
      <div className="pointer-events-none absolute top-1/3 -left-32 w-72 h-72 rounded-full bg-[#E8F5E9] blur-3xl"></div>

      <div className="relative max-w-md mx-auto min-h-screen flex flex-col px-6 pt-8 pb-8 bg-white shadow-sm">
        <div className="flex items-center gap-3">
          <Brand variant="full" to="/sc/signup" size={40} />
        </div>

        <div className="mt-7 flex items-center gap-2">
          <div className={"h-1 flex-1 rounded-full " + (step >= 1 ? "bg-[#F39C12]" : "bg-[#E8E2D6]")}></div>
          <div className={"h-1 flex-1 rounded-full " + (step >= 2 ? "bg-[#F39C12]" : "bg-[#E8E2D6]")}></div>
        </div>
        <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">
          Step {step} of 2 — {step === 1 ? "Phone" : "Verify OTP"}
        </div>

        <h1 className="text-4xl font-black leading-[1.05] mt-5">
          {step === 1 ? (
            <>Sign in to <span className="italic font-bold text-[#F39C12]">Purvodaya.</span></>
          ) : (
            <>Verify it's <span className="italic font-bold text-[#F39C12]">you.</span></>
          )}
        </h1>
        <p className="mt-3 text-sm text-[#475569] leading-relaxed">
          {step === 1
            ? "Enter your mobile number — new SCs get auto-onboarded, returning SCs are logged straight in. No password needed."
            : <>OTP sent to <span className="text-[#0F2A14] font-mono">{e164}</span>. {delivery === "mock" ? "Dev mode: ask admin to fetch from backend logs." : "Check your SMS inbox."}</>}
        </p>

        <div className="mt-8 flex-1">
          {step === 1 && (
            <form
              onSubmit={(e) => { e.preventDefault(); requestOtp(); }}
              className="space-y-5"
              data-testid="sc-signup-step-phone"
            >
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] mb-2">
                  Mobile number
                </label>
                <div className="flex items-center gap-2 bg-white border-2 border-[#E8E2D6] focus-within:border-[#F39C12] px-4 py-3.5 transition-colors">
                  <Phone className="w-4 h-4 text-[#1B5E20] flex-shrink-0" />
                  <span className="font-mono text-sm text-[#94A3B8]">+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    data-testid="sc-signup-phone"
                    placeholder="98765 43210"
                    className="flex-1 bg-transparent outline-none text-lg font-mono tracking-wider placeholder-[#94A3B8] text-[#0F2A14]"
                  />
                </div>
                {phone && e164 && (
                  <div className="font-mono text-[10px] text-[#94A3B8] mt-1.5">→ <span className="text-[#1B5E20] font-medium">{e164}</span></div>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] mb-2">
                  Your name <span className="text-[#94A3B8]">(optional)</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="sc-signup-name"
                  placeholder="So we know what to call you"
                  className="w-full bg-white border-2 border-[#E8E2D6] focus:border-[#F39C12] outline-none px-4 py-3.5 text-base text-[#0F2A14] placeholder-[#94A3B8] transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={!phoneValid || sending}
                data-testid="sc-signup-send-otp"
                className="w-full bg-[#1B5E20] text-white py-4 font-mono text-sm uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-transform hover:bg-[#0F3D14]"
              >
                {sending ? "Sending OTP…" : "Send OTP"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>

              <p className="text-[10px] font-mono text-[#94A3B8] leading-relaxed">
                By continuing you agree to receive transactional SMS from Purvodaya Solar. Standard carrier rates may apply.
              </p>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyOtp} className="space-y-5" data-testid="sc-signup-step-otp">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] mb-2">
                  6-digit OTP
                </label>
                <div className="flex items-center gap-2 bg-white border-2 border-[#E8E2D6] focus-within:border-[#F39C12] px-4 py-3 transition-colors">
                  <ShieldCheck className="w-4 h-4 text-[#1B5E20] flex-shrink-0" />
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="sc-signup-otp"
                    placeholder="••••••"
                    className="flex-1 bg-transparent outline-none text-2xl tracking-[0.6em] font-mono text-center text-[#0F2A14] placeholder-[#94A3B8]"
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="font-mono text-[10px] text-[#475569]">
                    {secondsLeft > 0 ? `Expires in ${Math.floor(secondsLeft / 60)}m ${(secondsLeft % 60).toString().padStart(2, "0")}s` : "OTP expired"}
                  </div>
                  <button
                    type="button"
                    onClick={requestOtp}
                    disabled={sending || secondsLeft > 250}
                    data-testid="sc-signup-resend-otp"
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F39C12] disabled:opacity-30 inline-flex items-center gap-1 hover:text-[#0F3D14]"
                  >
                    <RotateCcw className="w-3 h-3" /> Resend
                  </button>
                </div>
              </div>

              <details className="border-2 border-[#E8E2D6] bg-[#FAF6EE]">
                <summary className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#475569] cursor-pointer select-none">
                  Add city &amp; UPI <span className="text-[#94A3B8]">(or do it later)</span>
                </summary>
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-[#E8E2D6]">
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[#94A3B8] mb-1.5">City</label>
                    <input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      data-testid="sc-signup-city"
                      placeholder="Bengaluru"
                      className="w-full bg-transparent border-b border-[#D8D1BE] focus:border-[#F39C12] outline-none px-0 py-2 text-base text-[#0F2A14] placeholder-[#94A3B8]"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[#94A3B8] mb-1.5">UPI ID</label>
                    <input
                      value={upi}
                      onChange={(e) => setUpi(e.target.value)}
                      data-testid="sc-signup-upi"
                      placeholder="yourname@upi"
                      className="w-full bg-transparent border-b border-[#D8D1BE] focus:border-[#F39C12] outline-none px-0 py-2 text-base text-[#0F2A14] placeholder-[#94A3B8]"
                    />
                  </div>
                </div>
              </details>

              <button
                type="submit"
                disabled={verifying || code.length < 4}
                data-testid="sc-signup-verify-otp"
                className="w-full bg-[#1B5E20] text-white py-4 font-mono text-sm uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-transform hover:bg-[#0F3D14]"
              >
                {verifying ? "Verifying…" : "Verify & Continue"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>

              <button
                type="button"
                onClick={() => { setStep(1); setCode(""); }}
                data-testid="sc-signup-change-phone"
                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] text-[#475569] hover:text-[#0F2A14]"
              >
                <ChevronLeft className="w-3 h-3" /> Change phone number
              </button>
            </form>
          )}
        </div>

        <div className="pt-6 mt-6 border-t border-[#E8E2D6]">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">
            CRM staff?{" "}
            <Link to="/login" data-testid="sc-signup-staff-login" className="text-[#1B5E20] underline decoration-[#F39C12] underline-offset-2 font-semibold">
              Email + password sign-in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
